import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, Users } from "lucide-react";

// Assign one recruiter team (lead + collaborators) to MANY jobs at once.
export default function BulkAssignRecruitersDialog({ jobIds, recruiters, onClose, onSaved }) {
    const [picked, setPicked] = useState(() => new Set());
    const [leadId, setLeadId] = useState(null);
    const [mode, setMode] = useState("add");
    const [slaDays, setSlaDays] = useState("");
    const [saving, setSaving] = useState(false);

    const toggle = (uid) => setPicked((cur) => {
        const next = new Set(cur);
        if (next.has(uid)) { next.delete(uid); if (leadId === uid) setLeadId(null); }
        else next.add(uid);
        return next;
    });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                job_ids: jobIds,
                mode,
                assignments: [...picked].map((uid) => ({ user_id: uid, role: leadId === uid ? "lead" : "collaborator", targets: {} })),
            };
            if (slaDays !== "") payload.sla_days = Number(slaDays);
            const { data } = await api.post("/jobs/bulk-assign", payload);
            toast.success(`${data.updated} job${data.updated !== 1 ? "s" : ""} updated`);
            onSaved?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Users className="w-4 h-4 text-ikb" /> Assign recruiters · {jobIds.length} job{jobIds.length !== 1 ? "s" : ""}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {(recruiters || []).map((r) => {
                        const on = picked.has(r.id);
                        return (
                            <div key={r.id} className={`flex items-center gap-3 border p-2.5 transition-colors ${on ? "border-ikb bg-blue-50/40" : "border-border"}`}>
                                <input type="checkbox" checked={on} onChange={() => toggle(r.id)} className="w-4 h-4 accent-[#002FA7]" data-testid={`bulk-assign-check-${r.id}`} />
                                <div className="min-w-0 flex-1">
                                    <span className="font-medium text-sm">{r.name || r.email}</span>
                                    <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-foreground/60">{r.role}</span>
                                </div>
                                {on && (
                                    <button
                                        type="button"
                                        onClick={() => setLeadId(leadId === r.id ? null : r.id)}
                                        className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${leadId === r.id ? "border-amber-400 bg-amber-50 text-amber-700" : "border-border text-foreground/60 hover:border-amber-400 hover:text-amber-700"}`}
                                        data-testid={`bulk-assign-lead-${r.id}`}
                                    >
                                        <Star className={`w-3 h-3 ${leadId === r.id ? "fill-amber-500 text-amber-500" : ""}`} /> {leadId === r.id ? "Lead" : "Make lead"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center gap-4">
                    {[["add", "Add to existing team"], ["replace", "Replace team"]].map(([v, label]) => (
                        <label key={v} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                            <input type="radio" name="bulk-assign-mode" checked={mode === v} onChange={() => setMode(v)} className="accent-[#002FA7]" data-testid={`bulk-assign-mode-${v}`} />
                            {label}
                        </label>
                    ))}
                </div>
                {mode === "replace" && <p className="text-[11px] text-rose-700">Replace removes any recruiters currently on the selected jobs.</p>}
                {mode === "add" && leadId && <p className="text-[11px] text-foreground/60">The new lead will demote any existing lead to collaborator.</p>}

                <label className="block max-w-[200px]">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">SLA days (optional, applies to all)</span>
                    <input type="number" min="1" max="365" value={slaDays} onChange={(e) => setSlaDays(e.target.value)} placeholder="Keep current"
                        className="mt-1 w-full h-9 border border-border px-2 text-sm bg-white outline-none focus:border-ikb" data-testid="bulk-assign-sla" />
                </label>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={saving || picked.size === 0} className="bg-ikb hover:bg-[#001f7a]" data-testid="bulk-assign-save">
                        {saving ? "Assigning…" : `Assign to ${jobIds.length} job${jobIds.length !== 1 ? "s" : ""}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
